'use client';
import React from 'react';
import { IClientRetailFilters, ISearchTerms } from '@/types';
import { formatBrandName } from '@/utils/filterUtil';
import Typography from '@/components/shared/typography/typography.component';
import { CarTypesEnum } from '../car-details-v2/car-type';
import useTranslationHook from '@/hook/use-translation-client.hook';

interface CarsListingHeaderProps {
    filters: IClientRetailFilters;
    searchTerms: ISearchTerms;
    resultsCount: number;
}

const CarsListingHeader: React.FC<CarsListingHeaderProps> = ({
    filters,
    searchTerms,
    resultsCount,
}) => {
    const makeKeys = Object.keys(filters.makeModel);
    const categoryValue = filters.category;
    const { t, i18n } = useTranslationHook();
    const lang = i18n.language;
    const isArabic = lang === 'ar';
    const count = resultsCount;

    const formatCarsCount = (count: number, isArabic: boolean) => {
        if (isArabic) {
            if (count === 1 || count > 10) return 'عربية';
            return 'عربيات';
        } else {
            return count === 1 ? 'Car' : 'Cars';
        }
    };

    const carWord = formatCarsCount(count, isArabic);

    const getSelectedMakeAndModel = () => {
        if (makeKeys.length !== 1) return { make: '', model: '' };

        const makeKey = makeKeys[0];
        const makeObj = filters.makeModel[makeKey];
        const make = t(formatBrandName(makeObj.make));
        const selectedModels = Object.values(makeObj.models);

        const allModelsForMake = searchTerms.makeModelTree[makeKey];
        if (allModelsForMake) {
            const totalAvailableModels = Object.keys(
                allModelsForMake.models
            ).length;
            const selectedModelsCount = selectedModels.length;

            if (
                selectedModelsCount === totalAvailableModels &&
                totalAvailableModels > 1
            ) {
                return { make, model: '' };
            }
        }

        const model = selectedModels.length === 1 ? t(selectedModels[0]) : '';
        return { make, model };
    };

    const { make, model } = getSelectedMakeAndModel();

    const CountSpan = ({ children }: { children: React.ReactNode }) => (
        <Typography className="text-gray-800 text-body1SemiBold md:text-subtitle1SemiBold">
            {children}
        </Typography>
    );

    const getCategorySuffix = () => {
        if (!categoryValue || categoryValue === 'all') return '';

        const isSingle = count === 1;

        if (isArabic) {
            const arabicLabels: Record<CarTypesEnum, string> = {
                [CarTypesEnum.CERTIFIED]: 'معتمدة من سيلندر',
                [CarTypesEnum.LITE]: 'لايت من سيلندر',
                [CarTypesEnum.Dealer]: 'شريك معتمد',
            };

            return arabicLabels[categoryValue] || '';
        }

        const englishLabels: Record<
            CarTypesEnum,
            { single: string; plural: string }
        > = {
            [CarTypesEnum.CERTIFIED]: {
                single: 'Certified Car from Sylndr',
                plural: 'Certified Cars from Sylndr',
            },
            [CarTypesEnum.LITE]: {
                single: 'Lite Car from Sylndr',
                plural: 'Lite Cars from Sylndr',
            },
            [CarTypesEnum.Dealer]: {
                single: 'Certified partner Car',
                plural: 'Certified partner Cars',
            },
        };

        const label = englishLabels[categoryValue];
        return label ? (isSingle ? label.single : label.plural) : '';
    };

    const categorySuffix = getCategorySuffix();

    const TextContainer = ({ content }: { content: string }) => (
        <div className="flex gap-1.5 items-center">
            <CountSpan>{count}</CountSpan>
            <Typography className="text-gray-600 text-body2 md:text-subtitle2Medium">
                {content}
            </Typography>
        </div>
    );

    if (categorySuffix) {
        const textParts = isArabic
            ? [carWord, make, categorySuffix]
            : [make, categorySuffix];

        return <TextContainer content={textParts.filter(Boolean).join(' ')} />;
    }
    const getLabelParts = () => {
        if (isArabic) {
            return [carWord, make, model, categorySuffix, 'متاحة للبيع'];
        }

        return ['Available', make, model, carWord, categorySuffix, 'for Sale'];
    };
    if (make && model) {
        return (
            <TextContainer
                content={getLabelParts().filter(Boolean).join(' ')}
            />
        );
    }

    if (make) {
        const textParts = isArabic
            ? [carWord, make, categorySuffix, 'متاحة للبيع']
            : ['Available', make, carWord, categorySuffix, 'for Sale'];

        return <TextContainer content={textParts.filter(Boolean).join(' ')} />;
    }

    const textParts = isArabic
        ? [carWord, categorySuffix, 'متاحة للبيع']
        : ['Available', carWord, categorySuffix, 'for Sale'];

    return <TextContainer content={textParts.filter(Boolean).join(' ')} />;
};

export default CarsListingHeader;
